import React, { useState, useEffect, useContext } from 'react';
import { Table, Header, Box, Pagination, Button, TextFilter, CollectionPreferences } from '@amzn/awsui-components-react-v3';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { StateContext } from './context/spotcp-context';
import { SpotcpSummaryInfo, InfoLink } from './info';
import { PAGE_SIZE_OPTIONS, VISIBLE_CONTENT_OPTIONS, summaryColumnDefinitions } from './elements';
import '../../styles/spotcp.css';

export const Summary = (props) => {
  const { summaryDataLA, summaryDataFA } = useContext(StateContext);
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [preferences, setPreferences] = useState({
    pageSize: 35,
    visibleContent: VISIBLE_CONTENT_OPTIONS[0].options.map(opt => opt.id).filter(id => !id.endsWith('_la')),
    wraplines: true,
  });

  useEffect(() => {
    let merged = mergeFaAndLaSummaryRows();
    setSummaryData(merged);
  }, [summaryDataLA, summaryDataFA]);

  useEffect(() => {
    setLoading(summaryData ? false : true);
  }, [summaryData]);

  /**
   * backend returns list of object for both FA and LA: [{accuracy: xx, component: xx, spot: xx, hcp: xx}, {...}, {...}]
   * This helper function merge FA and LA data into one list
   */
  const mergeFaAndLaSummaryRows = () => {
    if (summaryDataFA && summaryDataLA) {
      let merged = summaryDataFA.map((fa, i) => mergeFaAndLaSummaryRow(fa, summaryDataLA[i]));
      return merged;
    };
  };

  /**
   * backend returns object with structure {accuracy: xx, component: xx, spot: xx, hcp: xx} for both FA and LA
   * This helper function rename the key and merge each of individual object like {accuracy_la: xx, accuracy_fa: xx, component_la: xx, component_fa: xx, ...}
   */
  const mergeFaAndLaSummaryRow = (fa, la) => {
    const faRenamed = Object.keys(fa).reduce((acc, key) => ({...acc, [key + '_fa']: fa[key]}), {});
    const laRenamed = Object.keys(la).reduce((acc, key) => ({...acc, [key + '_la']: la[key]}), {});
    return {...faRenamed, ...laRenamed};
  }
  
  const {items, actions, collectionProps, filterProps, paginationProps} = useCollection(
    summaryData || [],
    {
      filtering: {
        noMatch: (
          <EmptyState
            title="No matches"
            subtitle="We can’t find a match."
            action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
          />
        )
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: {}, // use default so pass empty object
    }
  )

  function EmptyState({ title, subtitle, action }) {
    return (
      <Box textAlign="center" color="inherit">
        <Box variant="strong" textAlign="center" color="inherit">
          {title}
        </Box>
        <Box variant="p" padding={{ bottom: 's' }} color="inherit">
          {subtitle}
        </Box>
        {action}
      </Box>
    );
  }

  return (
    <Table
      className='spotcp-summary'
      {...collectionProps}
      columnDefinitions={summaryColumnDefinitions}
      visibleColumns={preferences.visibleContent}
      items={items}
      loading={loading}
      resizableColumns
      wrapLines={preferences.wrapLines}
      loadingText="Loading Data..."
      filter={
        <TextFilter {...filterProps}/>
      }
      pagination={<Pagination {...paginationProps}/>}
      preferences={
        <CollectionPreferences
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          preferences={preferences}
          onConfirm={({ detail }) => setPreferences(detail)}
          pageSizePreference={{
            title: 'Page size',
            options: PAGE_SIZE_OPTIONS
          }}
          wrapLinesPreference={{
            label: 'Wrap lines',
            description: 'Check to see all the text and wrap the lines'
          }}
          visibleContentPreference={{
            title: 'Select visible columns',
            options: VISIBLE_CONTENT_OPTIONS
          }}
          />
      }
      header={
        <Header 
          variant="h2" 
          description="SPOT CP, HCP Unit value and Accuracy %"
          info={<InfoLink onFollow={() => props.updateTools(<SpotcpSummaryInfo />)} />}>
          Summary
        </Header>
      }
    />
  );
};



// WEBPACK FOOTER //
// ./src/components/spotcp/summary.jsx